import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, ShieldAlert, ShieldX, Loader2, ArrowRight } from "lucide-react";
import { Container, Eyebrow, DisplayHeading, BodyCopy, Reveal } from "@/components/kit";
import { MaskedLinesInView } from "@/components/MaskedLines";
import Certificate from "@/components/Certificate";
import { ScoreBar } from "@/components/TrustScore";
import { verifyCertificate } from "@/lib/api";

const SAMPLES = ["CERT-DEMO-000148", "CERT-DEMO-000149", "CERT-DEMO-000311"];

export default function Verify() {
  const [value, setValue] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | valid | expired | notfound | error
  const [cert, setCert] = useState(null);

  const run = async (id) => {
    const cid = (id ?? value).trim();
    if (!cid) return;
    setState("loading");
    setCert(null);
    try {
      await new Promise((r) => setTimeout(r, 900));
      const res = await verifyCertificate(cid);
      if (!res.found) {
        setState("notfound");
      } else {
        setCert(res.certificate);
        setState(res.certificate.status === "expired" ? "expired" : "valid");
      }
    } catch (e) {
      setState("error");
    }
  };

  return (
    <>
      <section className="relative overflow-hidden pb-12 pt-28 sm:pb-16 sm:pt-36 md:pt-44">
        <div className="pointer-events-none absolute inset-0 tech-grid radial-fade opacity-50" />
        <div className="pointer-events-none absolute -top-16 left-1/2 h-[260px] w-[400px] -translate-x-1/2 rounded-full bg-valid/[0.04] blur-[100px] sm:-top-24 sm:h-[420px] sm:w-[760px] sm:blur-[130px]" />
        <Container className="relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Eyebrow tone="valid">Public verification</Eyebrow>
          </motion.div>
          <DisplayHeading as="h1" className="mt-6 max-w-3xl text-[34px] leading-[1.06] sm:mt-7 sm:text-5xl md:text-6xl">
            <MaskedLinesInView lines={["Verify AI assurance."]} />
          </DisplayHeading>
          <Reveal i={1}>
            <BodyCopy className="mt-6 max-w-2xl">
              Enter a Meira certificate ID to independently confirm its authenticity, scope and
              current status. No login required.
            </BodyCopy>
          </Reveal>

          {/* input */}
          <Reveal i={2}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                run();
              }}
              className="mt-8 flex max-w-2xl flex-col gap-3 sm:mt-10 sm:flex-row"
            >
              <div className="relative flex-1">
                <input
                  data-testid="verify-input"
                  value={value}
                  onChange={(e) => setValue(e.target.value.toUpperCase())}
                  placeholder="CERT-XXXX-XXXXXX"
                  className="w-full rounded-full border border-line-bright bg-panel-raised px-5 py-4 font-mono text-base tracking-wider text-content placeholder:text-content-faint focus:border-brand/50 focus:outline-none sm:px-6 sm:text-sm"
                />
              </div>
              <button
                type="submit"
                data-testid="verify-submit"
                disabled={state === "loading"}
                className="inline-flex min-h-[52px] shrink-0 items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-soft disabled:opacity-60"
              >
                {state === "loading" ? <Loader2 className="animate-spin" size={16} /> : "Verify Certificate"}
              </button>
            </form>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Try:</span>
              {SAMPLES.map((s) => (
                <button
                  key={s}
                  data-testid={`verify-sample-${s}`}
                  onClick={() => {
                    setValue(s);
                    run(s);
                  }}
                  className="inline-flex min-h-[34px] items-center rounded-full border border-line bg-panel-raised px-3 py-1 font-mono text-[11px] tracking-wide text-content-muted transition-colors hover:border-brand/50 hover:text-content"
                >
                  {s}
                </button>
              ))}
            </div>
          </Reveal>
        </Container>
      </section>

      {/* result */}
      <Container className="relative pb-20 sm:pb-28">
        <AnimatePresence mode="wait">
          {state === "loading" && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-3 rounded-lg border border-line bg-panel-raised p-6 text-content-muted sm:p-8">
              <Loader2 className="animate-spin text-brand-soft" size={20} />
              <span className="font-mono text-sm tracking-wide">Running verification engine…</span>
            </motion.div>
          )}

          {(state === "valid" || state === "expired") && cert && (
            <motion.div key="valid" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-start">
              <div>
                <div className={`inline-flex items-center gap-2.5 rounded-full border px-4 py-2.5 sm:gap-3 sm:px-5 ${state === "valid" ? "border-valid/30 bg-valid/10 text-valid shadow-none" : "border-warn/30 bg-warn/10 text-warn"}`} data-testid="verify-result-status">
                  {state === "valid" ? <ShieldCheck size={20} /> : <ShieldAlert size={20} />}
                  <span className="font-display text-lg font-medium sm:text-xl">{state === "valid" ? "Verified" : "Certificate not current"}</span>
                </div>

                <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-line bg-panel-raised sm:grid-cols-2">
                  {[
                    ["Certificate ID", cert.certificate_id],
                    ["Organization", cert.organization],
                    ["AI System", cert.ai_system],
                    ["Assurance Level", cert.assurance_level],
                    ["Issued", cert.issued_date],
                    ["Valid Until", cert.valid_until],
                    ["Last Assessment", cert.last_assessment],
                    ["Assessment Type", cert.assessment_type],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-panel-raised p-4">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-content-faint">{k}</div>
                      <div className="mt-1.5 text-sm text-content">{v}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-col gap-4 rounded-lg border border-line bg-panel-raised p-5 sm:p-6">
                  <ScoreBar label="Security coverage" value={cert.security_coverage} tone="valid" />
                  <ScoreBar label="Governance coverage" value={cert.governance_coverage} tone="brand" delay={0.1} />
                  <ScoreBar label="Framework readiness" value={cert.framework_readiness} tone="gold" delay={0.2} />
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-valid/20 bg-valid/5 p-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-valid">What this means</div>
                    <p className="mt-2 text-[13px] leading-relaxed text-content-muted">
                      Meira independently assessed the named AI system and it met the assurance level
                      shown, with dated evidence supporting each result.
                    </p>
                  </div>
                  <div className="rounded-lg border border-line bg-panel-raised p-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">What it does not mean</div>
                    <p className="mt-2 text-[13px] leading-relaxed text-content-muted">
                      This is a Meira AI assurance certificate — not an ISO/IEC 42001 certificate
                      issued by an accredited certification body.
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:sticky lg:top-28">
                <Certificate cert={cert} />
              </div>
            </motion.div>
          )}

          {state === "notfound" && (
            <motion.div key="notfound" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="max-w-xl rounded-lg border border-line bg-panel-raised p-6 sm:p-8" data-testid="verify-result-notfound">
              <div className="inline-flex items-center gap-3 text-content-muted">
                <ShieldX size={22} className="text-content-faint" />
                <span className="font-display text-xl text-content">Certificate not found</span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-content-muted">
                We couldn’t find a certificate with that ID. Check for typos, confirm you copied the
                full ID, or ask the issuer to re-share it. Certificate IDs follow the format
                <span className="font-mono text-content"> CERT-YYYY-NNNNNN</span>.
              </p>
            </motion.div>
          )}

          {state === "error" && (
            <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-xl rounded-lg border border-danger/20 bg-danger/5 p-6 text-content-muted sm:p-8">
              Something went wrong reaching the verification engine. Please try again.
            </motion.div>
          )}

          {state === "idle" && (
            <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 rounded-lg border border-dashed border-line bg-panel-raised/40 p-6 text-content-faint sm:p-8">
              <ArrowRight size={18} />
              <span className="text-sm">Enter a certificate ID above, or tap a sample, to see a verified result.</span>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </>
  );
}
